/**
 * Inverse of `mermaid-emitter.ts`: parses Mermaid `flowchart` text into a
 * typed ParseResult. The grammar is narrow — only the syntax our own
 * emitter produces (and a couple of common variations a user might type).
 *
 * Recognised forms:
 *   flowchart LR | TB | RL | BT
 *   classDef <slug> fill:…,stroke:…,…
 *   %% type:archimate.Foo            (preceding comment carries the typeId)
 *   <id>["Label"]:::class
 *   <id>("Label"):::class
 *   <id>{"Label"}:::class
 *   <id> -->|"label"| <id>
 *   <id> -.->|"label"| <id>
 *   <id> --> <id>                    (no label, defaults to Association)
 *
 * Skipped (whitespace, comments, layout): blank lines, `%% …`, `subgraph
 * …` / `end`, `class …` styling.
 *
 * IMPORTANT: Mermaid only allows `%%` comments on their own line — inline
 * comments after a node/edge corrupt the parser. The emitter and parser
 * therefore put each `%% type:…` on its own line *preceding* the
 * node/edge it annotates. The parser tracks a "pending typeId" and
 * applies it to the next node/edge it sees.
 */
import type { ParsedConcept, ParsedRelation, ParseError, ParseResult } from "./archimate-parser";

const SHAPE_OPENERS: Record<string, string> = { '["': '"]', '("': '")', '{"': '"}' };

export function parseArchiMateMermaid(text: string): ParseResult {
  const concepts: ParsedConcept[] = [];
  const relations: ParsedRelation[] = [];
  const errors: ParseError[] = [];
  const lines = text.split(/\r?\n/);

  // Holds the typeId from the most recent `%% type:…` comment so the
  // parser can apply it to the next node/edge. Cleared on consume.
  let pendingTypeId: string | null = null;

  for (let i = 0; i < lines.length; i++) {
    const lineNum = i + 1;
    const raw = lines[i] ?? "";
    const trimmed = raw.trim();
    if (trimmed === "") continue;

    // Comment lines: extract `type:archimate.Foo` and stash for the next
    // non-comment line. Other comments (e.g. `source=part target=whole`)
    // are decorative and silently skipped.
    if (trimmed.startsWith("%%")) {
      const body = trimmed.slice(2).trim();
      const typeMatch = body.match(/^type:([\w.]+)\s*$/);
      if (typeMatch?.[1]) pendingTypeId = typeMatch[1];
      continue;
    }

    if (/^flowchart\s+(LR|TB|RL|BT|TD)\s*$/i.test(trimmed)) continue;
    if (/^classDef\s+\w+/i.test(trimmed)) continue;
    if (/^linkStyle\s+/i.test(trimmed)) continue;
    if (/^style\s+/i.test(trimmed)) continue;
    if (/^subgraph\s/i.test(trimmed)) continue;
    if (/^end\s*$/i.test(trimmed)) continue;
    if (/^class\s+/i.test(trimmed)) continue;

    // Try edge first — has an arrow `-->` or `-.->` somewhere in the line.
    const edge = parseEdge(trimmed, pendingTypeId, lineNum);
    if (edge) {
      relations.push(edge);
      pendingTypeId = null;
      continue;
    }

    // Try node next.
    const node = parseNode(trimmed, pendingTypeId, lineNum);
    if (node) {
      concepts.push(node);
      pendingTypeId = null;
      continue;
    }

    errors.push({ line: lineNum, message: `kan regel niet parsen: ${trimmed.slice(0, 80)}` });
  }

  return { concepts, relations, errors };
}

/** Match a node definition. Returns null if the line isn't a node-def. */
function parseNode(
  line: string,
  pendingTypeId: string | null,
  lineNum: number,
): ParsedConcept | null {
  // <id><openShape><label><closeShape>(:::<class>)?
  // Examples:
  //   n_abc["Customer"]:::business
  //   capability("Strategy")
  const match = line.match(/^([A-Za-z_][\w]*)(\["|\("|\{")(.*?)("\]|"\)|"\})(\s*:::\s*(\w+))?\s*$/);
  if (!match) return null;
  const [, id, opener, rawLabel, closer] = match;
  if (!id || !opener || !closer || rawLabel === undefined) return null;
  if (SHAPE_OPENERS[opener] !== closer) return null;
  const label = rawLabel.replace(/&quot;/g, '"');
  // typeId comes from the preceding `%% type:…` comment if any, else
  // empty so applyParseResult can resolve it from an existing concept.
  return { alias: id, typeId: pendingTypeId ?? "", label, line: lineNum };
}

/** Match an edge. Returns null if the line isn't an edge. */
function parseEdge(
  line: string,
  pendingTypeId: string | null,
  lineNum: number,
): ParsedRelation | null {
  // <fromId> <arrow>[|"label"|] <toId>
  // arrow ∈ { -->, -.-> }
  const match = line.match(
    /^([A-Za-z_][\w]*)\s+(-->|-\.->)(?:\s*\|\s*"(.*?)"\s*\|)?\s+([A-Za-z_][\w]*)\s*$/,
  );
  if (!match) return null;
  const [, sourceAlias, arrow, rawLabel, targetAlias] = match;
  if (!sourceAlias || !targetAlias || !arrow) return null;
  const labelRaw = (rawLabel ?? "").replace(/&quot;/g, '"');
  // typeId precedence: explicit `%% type:` comment > arrow-based default.
  let typeId = pendingTypeId ?? "archimate.Association";
  if (!pendingTypeId && arrow === "-.->") {
    // No explicit type and dashed arrow → assume Realization.
    typeId = "archimate.Realization";
  }
  return { sourceAlias, targetAlias, typeId, label: labelRaw.trim(), line: lineNum };
}
